import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Patient, PatientDailyCheckIn } from "types/Patient";
import { FilterParams, SortConfig, SortDirection } from "types/common";
import { PATIENTS_LIMIT } from "constants/dashboard";
import { ExamItem, ExamMLResult } from "types/Exam";
import { PatientRegionDetail } from "../../types/PatientRegion";

export interface PatientState {
  currentPatient: Patient;
  currentPatientDailyCheckin: PatientDailyCheckIn;
  currentPatientDailyCheckinWeek: PatientDailyCheckIn;
  currentPatientDailyCheckinMonth: PatientDailyCheckIn;
  currentPatientDailyCheckinYear: PatientDailyCheckIn;
  examList: ExamItem[];
  sortConfig: SortConfig;
  patientRegionDetails: PatientRegionDetail[];
  examMLResultLeftKnee: ExamMLResult[];
  examMLResultRightKnee: ExamMLResult[];
  examMLResultDoubleKnee: ExamMLResult[];
  jointFilterParams: FilterParams[];
  typeFilterParams: FilterParams[];
  limit: number;
  offset: number;
  loading: boolean;
  totalExams: number;
  patientSportsUploading: boolean;
  athleteIdInProcess: string;
  sportFilterCategories: string[];
  clinicPatientsList: Patient[];
  limitClinicPatients: number;
  offsetClinicPatients: number;
  totalClinicPatients: number;
  examDateRange: string;
  sortConfigClinicPatients: SortConfig;
  search: string;
  jointOptions?: { label: string; value: string }[]
}

const initialState: PatientState = {
  currentPatient: {} as Patient,
  currentPatientDailyCheckin: {} as PatientDailyCheckIn,
  currentPatientDailyCheckinWeek: {} as PatientDailyCheckIn,
  currentPatientDailyCheckinMonth: {} as PatientDailyCheckIn,
  currentPatientDailyCheckinYear: {} as PatientDailyCheckIn,
  examList: [],
  patientRegionDetails: [],
  examMLResultLeftKnee: [],
  examMLResultRightKnee: [],
  examMLResultDoubleKnee: [],
  sortConfig: { sortBy: "", sortDirection: SortDirection.desc },
  jointFilterParams: [],
  typeFilterParams: [],
  limit: PATIENTS_LIMIT,
  offset: 0,
  loading: false,
  examDateRange: "all",
  totalExams: 0,
  patientSportsUploading: false,
  athleteIdInProcess: "",
  sportFilterCategories: [],
  clinicPatientsList: [],
  totalClinicPatients: 0,
  limitClinicPatients: PATIENTS_LIMIT,
  offsetClinicPatients: 0,
  sortConfigClinicPatients: { sortBy: "", sortDirection: SortDirection.desc },
  search: "",
  jointOptions: [],
};

export const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    setCurrentPatient: (
      state,
      { payload }: PayloadAction<PatientState["currentPatient"]>
    ) => {
      state.currentPatient = payload;
    },
    setCurrentPatientDailyCheckinData: (
      state,
      { payload }: PayloadAction<PatientState["currentPatientDailyCheckin"]>
    ) => {
      state.currentPatientDailyCheckin = payload;
    },
    setCurrentPatientDailyCheckinWeekData: (
      state,
      { payload }: PayloadAction<PatientState["currentPatientDailyCheckinWeek"]>
    ) => {
      state.currentPatientDailyCheckinWeek = payload;
    },
    setCurrentPatientDailyCheckinMonthData: (
      state,
      { payload }: PayloadAction<PatientState["currentPatientDailyCheckinMonth"]>
    ) => {
      state.currentPatientDailyCheckinMonth = payload;
    },
    setCurrentPatientDailyCheckinYearData: (
      state,
      { payload }: PayloadAction<PatientState["currentPatientDailyCheckinYear"]>
    ) => {
      state.currentPatientDailyCheckinYear = payload;
    },
    cleanCurrentPatientInfo: (
      state,
    ) => {
      state.currentPatient = initialState.currentPatient;
      state.examList = initialState.examList;
      state.patientRegionDetails = initialState.patientRegionDetails;
      state.examMLResultLeftKnee = initialState.examMLResultLeftKnee;
      state.examMLResultRightKnee = initialState.examMLResultRightKnee;
      state.examMLResultDoubleKnee = initialState.examMLResultDoubleKnee;
      state.sortConfig = initialState.sortConfig;
      state.jointFilterParams = initialState.jointFilterParams;
      state.typeFilterParams = initialState.typeFilterParams;
      state.limit = initialState.limit;
      state.offset = initialState.offset;
      state.loading = initialState.loading;
      state.totalExams = initialState.totalExams;
      state.patientSportsUploading = initialState.patientSportsUploading;
      state.athleteIdInProcess = initialState.athleteIdInProcess;
      state.limitClinicPatients = initialState.limit;
      state.offsetClinicPatients = initialState.offset;
      state.totalClinicPatients = initialState.totalClinicPatients;
      state.sortConfigClinicPatients = initialState.sortConfigClinicPatients;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setPatientSportsUploading: (state, { payload }: PayloadAction<boolean>) => {
      state.patientSportsUploading = payload;
    },
    setAthleteIdInProcess: (state, { payload }: PayloadAction<string>) => {
      state.athleteIdInProcess = payload;
    },
    setSortConfig: (state, { payload }: PayloadAction<SortConfig>) => {
      state.sortConfig = payload;
    },
    setOffset: (state, { payload }: PayloadAction<number>) => {
      state.offset = payload;
    },
    setLimit: (state, { payload }: PayloadAction<number>) => {
      state.limit = payload;
    },
    setTotalExams: (state, { payload }: PayloadAction<number>) => {
      state.totalExams = payload;
    },
    setClinicPatientsList: (
      state,
      { payload }: PayloadAction<Patient[]>
    ) => {
      state.clinicPatientsList = payload;
    },
    setTotalClinicPatients: (state, { payload }: PayloadAction<number>) => {
      state.totalClinicPatients = payload;
    },
    setSortConfigClinicPatients: (state, { payload }: PayloadAction<SortConfig>) => {
      state.sortConfigClinicPatients = payload;
    },
    setOffsetClinicPatients: (state, { payload }: PayloadAction<number>) => {
      state.offsetClinicPatients = payload;
    },
    setLimitClinicPatients: (state, { payload }: PayloadAction<number>) => {
      state.limitClinicPatients = payload;
    },
    setExamList: (
      state,
      { payload }: PayloadAction<PatientState["examList"]>
    ) => {
      state.examList = payload;
    },
    setExamDateRange: (
        state,
        { payload }: PayloadAction<PatientState["examDateRange"]>
    ) => {
      state.examDateRange = payload;
    },
    setPatientRegionDetails: (
      state,
      { payload }: PayloadAction<any>
    ) => {
      state.patientRegionDetails = payload.list ?? [];
      state.examMLResultLeftKnee = payload.examMLResultLeftKnee ?? [];
      state.examMLResultRightKnee = payload.examMLResultRightKnee ?? [];
      state.examMLResultDoubleKnee = payload.examMLResultDoubleKnee ?? [];
    },
    setJointFilterParams: (
      state,
      { payload }: PayloadAction<FilterParams[]>
    ) => {
      state.jointFilterParams = payload;
    },
    setTypeFilterParams: (
      state,
      { payload }: PayloadAction<FilterParams[]>
    ) => {
      state.typeFilterParams = payload;
    },
    setSportFilterCategories: (state, { payload }: PayloadAction<string[]>) => {
      state.sportFilterCategories = payload;
    },
    setSearch: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.search = payload;
    },
    setJointOptions: (
      state,
      { payload }: PayloadAction<{ label: string; value: string; }[]>
    ) => {
      state.jointOptions = payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setCurrentPatient,
  setCurrentPatientDailyCheckinData,
  setCurrentPatientDailyCheckinWeekData,
  setCurrentPatientDailyCheckinMonthData,
  setCurrentPatientDailyCheckinYearData,
  setLoading,
  setPatientSportsUploading,
  setSortConfig,
  setExamList,
  setExamDateRange,
  setOffset,
  setLimit,
  setTotalExams,
  setPatientRegionDetails,
  cleanCurrentPatientInfo,
  setJointFilterParams,
  setTypeFilterParams,
  setAthleteIdInProcess,
  setSportFilterCategories,
  setClinicPatientsList,
  setTotalClinicPatients,
  setSortConfigClinicPatients,
  setOffsetClinicPatients,
  setLimitClinicPatients,
  setSearch,
  setJointOptions,
} = patientSlice.actions;
export const { reducer } = patientSlice;
